'use client';

import React, { useState } from 'react';
import classnames from 'classnames';
import useMediaQuery, { BreakPointToken } from '../../hooks/use-media-query';
import {
  IconTravelPlaneComponent,
  IconActionTimeComponent,
  IconNavigationChevronArrowUpComponent,
  IconNavigationChevronArrowDownComponent,
  IconsSize,
} from '@vaad1st/component.atom.icons';
import {
  TypographyComponent,
  TypographySize,
  TypographyVariant,
  TypographyWeight,
} from '@vaa-component-lib/component.atom.typography';
import { AlertComponent, AlertStatus } from '@vaad1st/component.molecule.alert';
import style from './flying-days.component.module.less';
import {
  ButtonComponent,
  ButtonPriority,
  ButtonSize,
  ButtonType,
  IconPosition,
} from '@vaad1st/component.atom.button';

export interface FlyingDaysComponentProps {
  componentId?: string;
  locationTitle?: string;
  locationSubTitle?: string;
  flyingDays?: {
    locationName?: string;
    airportCode?: string;
    flightOperatingDates?: string;
    mon: boolean;
    tues: boolean;
    wed: boolean;
    thurs: boolean;
    fri: boolean;
    sat: boolean;
    sun: boolean;
  }[];
  hideLegend?: boolean;
  showAlert?: boolean;
  alertTitle?: string;
  alertText?: string;
  alertLinkText?: string;
  alertLink?: string;
  children?: React.ReactNode;
}

const FlyingDaysComponent: React.FC<FlyingDaysComponentProps> = ({
  componentId,
  locationTitle,
  locationSubTitle,
  flyingDays = [],
  hideLegend,
  showAlert,
  alertTitle,
  alertText,
  alertLinkText,
  alertLink,
  children,
}) => {
  const [showAllRoutes, setShowAllRoutes] = useState(false);
  const isDesktop = useMediaQuery(`(min-width: ${BreakPointToken.MdMax}px)`);
  console.log('isDesktop', isDesktop);
  const daysOfWeek = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const fullDaysOfWeek = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];
  const visibleFlyingDays =
    !isDesktop && !showAllRoutes ? flyingDays.slice(0, 2) : flyingDays;
  const hasMoreThanTwoLocations = flyingDays.length > 2;

  const handleToggleRoutes = () => {
    setShowAllRoutes((prev) => !prev);
  };

  return (
    <div id={componentId} className={style['flying-days']}>
      {locationTitle && (
        <div className={style['header']}>
          <div className={style['header-location']}>
            <IconTravelPlaneComponent size={IconsSize.Lrg} />
            <TypographyComponent
              variant={TypographyVariant.Body}
              size={TypographySize.Lrg}
              weight={TypographyWeight.Medium}
            >
              {locationTitle}
            </TypographyComponent>
          </div>

          <div className={style['header-flight-duration']}>
            <IconActionTimeComponent />
            <TypographyComponent
              variant={TypographyVariant.Body}
              size={TypographySize.Mdm}
              weight={TypographyWeight.Regular}
            >
              {locationSubTitle}
            </TypographyComponent>
          </div>
        </div>
      )}

      <div className={style['days-header']}>
        <div className={style['empty-area']} />
        <div className={style['days']}>
          {daysOfWeek.map((day, index) => (
            <div
              key={index}
              className={style['day']}
              aria-label={fullDaysOfWeek[index]}
            >
              <TypographyComponent
                element="p"
                variant={TypographyVariant.Body}
                size={TypographySize.Xsm}
                weight={TypographyWeight.Regular}
                aria-hidden="true"
              >
                {day}
              </TypographyComponent>
            </div>
          ))}
        </div>
      </div>

      <div className={style['location-wrapper']}>
        {visibleFlyingDays.map(
          (
            {
              locationName,
              airportCode,
              flightOperatingDates,
              mon,
              tues,
              wed,
              thurs,
              fri,
              sat,
              sun,
            },
            index
          ) => (
            <div key={index} className={style['location-block']}>
              <div className={style['location-header']}>
                <div className={style['location-name']}>
                  <TypographyComponent
                    element="p"
                    variant={TypographyVariant.Body}
                    size={TypographySize.Xsm}
                    weight={TypographyWeight.Regular}
                  >
                    {locationName}
                    {airportCode ? ` (${airportCode})` : ''}
                  </TypographyComponent>
                </div>
                <div className={style['flying-dates']}>
                  <TypographyComponent
                    element="p"
                    variant={TypographyVariant.Body}
                    size={TypographySize.Xsm}
                    weight={TypographyWeight.Regular}
                  >
                    {flightOperatingDates}
                  </TypographyComponent>
                </div>
              </div>

              <div className={style['location-days']}>
                {[mon, tues, wed, thurs, fri, sat, sun].map(
                  (available, idx) => (
                    <div
                      key={idx}
                      className={classnames(
                        style['day'],
                        available ? style['available'] : style['unavailable']
                      )}
                    />
                  )
                )}
              </div>
            </div>
          )
        )}
      </div>

      {!isDesktop && hasMoreThanTwoLocations && (
        <div className={style['show-more-toggle']}>
          <ButtonComponent
            type={ButtonType.Link}
            size={ButtonSize.Small}
            priority={ButtonPriority.Primary}
            onClick={handleToggleRoutes}
            text={showAllRoutes ? 'View less' : 'View more'}
            iconChild={showAllRoutes ? <IconNavigationChevronArrowUpComponent /> : <IconNavigationChevronArrowDownComponent/>}
            iconPosition={IconPosition.Right}
          />
        </div>
      )}

      {!hideLegend && (
        <div className={style['legend']}>
          <LegendItem label="Virgin flights available" isAvailable />
          <LegendItem
            label="No Virgin flights, partner airlines may fly"
            isAvailable={false}
          />
        </div>
      )}

      {showAlert && (
        <div className={style['alert-banner']}>
          <AlertComponent
            title={alertTitle}
            hasIcon
            linkHref={alertLink}
            linkText={alertLinkText}
            status={AlertStatus.System}
          >
            <TypographyComponent
              element="p"
              variant={TypographyVariant.Body}
              size={TypographySize.Xsm}
              weight={TypographyWeight.Regular}
            >
              {alertText}
            </TypographyComponent>
          </AlertComponent>
        </div>
      )}

      {children && typeof children === 'string' ? (
        <div
          className={style['additional-text']}
          dangerouslySetInnerHTML={{ __html: children }}
        />
      ) : (
        <div className={style['additional-text']}>{children}</div>
      )}
    </div>
  );
};

const LegendItem = ({
  label,
  isAvailable,
}: {
  label: string;
  isAvailable: boolean;
}) => (
  <div className={style['legend-item']}>
    <div
      className={classnames(
        style['legend-icon'],
        isAvailable ? style['available'] : style['unavailable']
      )}
    />
    <TypographyComponent
      element="p"
      variant={TypographyVariant.Body}
      size={TypographySize.Xsm}
      weight={TypographyWeight.Regular}
    >
      {label}
    </TypographyComponent>
  </div>
);

export default FlyingDaysComponent;
